import React from "react";
import MainAppBar from "../../components/atoms/appbar/MainAppbar";
import AnimatedCoverTabs from "../../components/atoms/tab/AnimatedConverTab";
import { useNavigate, useSearchParams } from "react-router-dom";
import { vw } from "../../lib/utils/responsiveUtils";
import Button from "../../components/atoms/button";
import { diffClassAny } from "../../lib/utils/score.utils";
import { useGetScoreList } from "../../@apis/hooks/useScore";
import { useQuery } from "@tanstack/react-query";
import type { ScoreListItem } from "../../@apis/dto/response/score.response";
import { cn } from "../../lib/utils/cn";
import { formatKoreanDate } from "../../lib/utils/dateUtils";
import ScoreTypeDrawer from "../../components/atoms/drawer/ScoreTypeDrawer";
import type { ScoreType } from "../../types/score.types";
import ScoreFloatingButton from "../../components/atoms/button/ScoreFloatingButton";
import { useLoginInfo } from "../../hooks/useLoginInfo";
import { LOGIN_DIALOG_LABEL } from "../../lib/constants/common.constants";
import { useModalStore } from "../../lib/store/useModalStore";
import { useRouter } from "../../hooks/useRouter";
import { saveScrollPosition } from "../../hooks/useMainScrollRestoration";

const MIN_YEAR = 2022;

const CURRENT_YEAR = new Date().getFullYear();

const TAB_LABELS = Array.from(
  { length: CURRENT_YEAR - MIN_YEAR + 1 },
  (_, idx) => {
    const year = CURRENT_YEAR - idx;
    return {
      label: `${year}년`,
      value: String(year),
    };
  },
);

export default function Score() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = React.useState(false);

  const year =
    searchParams.get("year") ?? TAB_LABELS[0]?.value ?? String(CURRENT_YEAR);
  const selectedType = (searchParams.get("type") as ScoreType) ?? "all";
  const isComplete = searchParams.get("isComplete") === "true";

  const form = {
    year,
    isComplete,
    type: selectedType !== "all" ? selectedType : undefined,
  };

  const { isLoggedIn } = useLoginInfo();
  const { openModal } = useModalStore();
  const { push } = useRouter();

  const { data: scoreListResponse } = useQuery(useGetScoreList(form));
  const scoreCards = scoreListResponse?.data?.scoreCardList ?? [];
  const hasScores = (scoreCards?.length ?? 0) > 0;

  // 스크롤 복원: 상세에서 돌아온 경우에만, 데이터 로드 후 실행
  const cameFromDetailRef = React.useRef(
    sessionStorage.getItem("score_came_from_detail") === "true",
  );
  const scrollRestoredRef = React.useRef(false);

  React.useEffect(() => {
    if (!cameFromDetailRef.current) {
      sessionStorage.removeItem("scroll_score");
    }
    sessionStorage.removeItem("score_came_from_detail");
  }, []);

  React.useEffect(() => {
    if (!cameFromDetailRef.current || scrollRestoredRef.current) return;
    if (scoreCards.length === 0) return;
    const saved = sessionStorage.getItem("scroll_score");
    const mainEl = document.querySelector("main") as HTMLElement | null;
    if (saved && mainEl) {
      mainEl.scrollTop = Number(saved);
      scrollRestoredRef.current = true;
    }
  }, [scoreCards]);

  // roundMonth로 그룹화
  const monthMap = new Map<number, ScoreListItem[]>();
  for (const s of scoreCards) {
    const m = s.roundMonth;
    if (!monthMap.has(m)) monthMap.set(m, []);
    monthMap.get(m)!.push(s);
  }
  const months = Array.from(monthMap.keys()).sort((a, b) => a - b);

  return (
    <div className="min-h-full bg-[#f5f5f5] relative flex flex-col">
      <MainAppBar title="스코어" search={false} />
      <div className="absolute top-[56px] left-0 w-full h-[60px] bg-gradient-to-t from-transparent to-white pointer-events-none" />

      <div className="px-[15px] flex-1 flex flex-col">
        {/* 상단 탭 */}
        <AnimatedCoverTabs
          tabs={TAB_LABELS}
          value={year}
          maxVisible={4}
          onChange={(value) =>
            setSearchParams(
              (prev) => {
                const n = new URLSearchParams(prev);
                n.set("year", value);
                return n;
              },
              { replace: true },
            )
          }
          className="z-10"
        />

        <section className="mt-[15px] flex-1 flex flex-col">
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="white"
              onClick={() => setOpen(true)}
              className="flex items-center justify-center gap-[6px] px-[14px] rounded-full border border-[#e6e6e6] bg-[#fff] text-[16px]"
            >
              <span className="text-[#222] font-[500]">
                {selectedType === "all"
                  ? "전체"
                  : selectedType === "NORMAL"
                    ? "일반"
                    : "대회"}
              </span>
              <img
                className="w-[12px] object-contain"
                src="/icons/ic-dropdown.webp"
                alt="전체"
              />
            </Button>

            <Button
              type="button"
              variant={isComplete ? "black" : "white"}
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const n = new URLSearchParams(prev);
                    if (!isComplete) n.set("isComplete", "true");
                    else n.delete("isComplete");
                    return n;
                  },
                  { replace: true },
                )
              }
              className="flex items-center justify-center gap-[10px] px-[14px] rounded-full border border-[#e6e6e6] font-[500]"
            >
              <img
                src={
                  isComplete
                    ? "/icons/ic-check.webp"
                    : "/icons/ic-check-gray.webp"
                }
                alt="완료"
                style={{ width: vw(14), height: vw(10) }}
              />
              <span
                className={cn(
                  "text-[16px]",
                  isComplete ? "text-[#fff]" : "text-[#222]",
                )}
              >
                완료 스코어만 보기
              </span>
            </Button>
          </div>

          <div className="mt-[25px] mb-[19px]">
            <span className="text-[#222] font-bold text-2xl">{year}년</span>
          </div>

          {hasScores ? (
            <>
              {/* 월별 그룹 출력 */}
              {months.map((month) => {
                const list = monthMap.get(month) ?? [];
                return (
                  <div key={month} className="flex gap-[10px] mb-[35px]">
                    {/* 좌측: 월 라벨 */}
                    <div className="relative shrink-0">
                      <div className="text-[#222] font-bold text-center leading-none">
                        {month}월
                      </div>
                      <div
                        className="absolute left-1/2 -translate-x-1/2 top-6 bottom-[-15px]"
                        style={{
                          width: "2px",
                          backgroundImage:
                            "radial-gradient(circle, #e6e6e6 1px, transparent 1px)",
                          backgroundSize: "2px 6px",
                        }}
                      />
                    </div>

                    {/* 우측: 카드 리스트 */}
                    <div className="flex-1">
                      <div className="space-y-3">
                        {list.map((d, idx) => {
                          return (
                            <div
                              key={idx}
                              className="rounded-[15px] bg-[#fff] p-[19px] flex cursor-pointer gap-x-[15px]"
                              onClick={() => {
                                saveScrollPosition("score");
                                sessionStorage.setItem(
                                  "score_came_from_detail",
                                  "true",
                                );
                                navigate(`/score/${d.id}`);
                              }}
                            >
                              <div
                                className="flex items-center justify-center rounded-full bg-[#e6e6e6] flex-shrink-0 mt-[5px]"
                                style={{ width: vw(48), height: vw(48) }}
                              >
                                <img
                                  src={
                                    d.tournamentId
                                      ? "/icons/ic-tournament.webp"
                                      : "/icons/ic-golf.webp"
                                  }
                                  alt={d.tournamentId ? "대회" : "일반"}
                                  style={{ width: vw(20), height: vw(23) }}
                                />
                              </div>
                              <div className="min-w-0 flex flex-col">
                                <p className="text-[#222] font-bold text-xl mb-[5px]">
                                  {d.tournamentName
                                    ? d.tournamentName
                                    : d.golfParkName}
                                </p>
                                <div className="flex gap-[4px] text-[#888] font-[500] w-fit">
                                  <img
                                    className="size-3.5 mt-[4px]"
                                    src={
                                      d.isComplete
                                        ? "/icons/ic-check-red.webp"
                                        : "/icons/ic-write-black.webp"
                                    }
                                    alt=""
                                  />
                                  <span className={cn("text-sm text-[#888]")}>
                                    {formatKoreanDate(d.roundDate)}{" "}
                                    {!d.isComplete ? "(입력중)" : ""}
                                  </span>
                                </div>
                                <p className="text-[#222] line-clamp-1 text-xs mt-1">
                                  {d.roundName}
                                </p>

                                <div className="flex-shrink-0 font-bold  ">
                                  <p
                                    className={cn(
                                      "text-2xl leading-[150%] flex items-center",
                                      d.isComplete
                                        ? "text-[#222]"
                                        : "text-[#888]",
                                    )}
                                  >
                                    {d.totalScore}
                                    {d.isComplete && (
                                      <span
                                        className={`font-[500] text-base leading-[150%] ${diffClassAny(d.totalUnderOverScore ?? 0)}`}
                                      >
                                        (
                                        {d.totalUnderOverScore == null
                                          ? ""
                                          : d.totalUnderOverScore > 0
                                            ? `+${d.totalUnderOverScore}`
                                            : d.totalUnderOverScore}
                                        )
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center pb-[50px] text-[#ccc]">
              <img
                src={"/icons/ic-notice.webp"}
                alt="스코어 목록"
                className="mb-[14px]"
                style={{ width: vw(48), height: vw(48) }}
              />
              <p>스코어 내역이 없습니다.</p>
            </div>
          )}
        </section>
      </div>

      <ScoreTypeDrawer
        open={open}
        onClose={() => setOpen(false)}
        selectedType={selectedType}
        onSelect={(type) =>
          setSearchParams(
            (prev) => {
              const n = new URLSearchParams(prev);
              if (type === "all") n.delete("type");
              else n.set("type", type);
              return n;
            },
            { replace: true },
          )
        }
      />

      <div className="fixed bottom-[77px] right-[15px] z-50">
        <ScoreFloatingButton
          onClick={() =>
            !isLoggedIn
              ? openModal("CONFIRM_DIALOG", {
                  title: LOGIN_DIALOG_LABEL.title,
                  content: LOGIN_DIALOG_LABEL.content,
                  onClick: () => push("/login"),
                })
              : navigate("/score/add")
          }
        />
      </div>
    </div>
  );
}
